import React from "react";

import mapboxgl from 'mapbox-gl';

import { DateTime, Interval } from 'luxon';

import { pairwise, zip, timeIsKnown } from "./utils.js";
import { SelectionInterval } from "./SelectionInterval.js";
import { hex2rgba, rgba2hex } from "./environment/utils.js";

import mapboxgl_config from "./json/mapboxgl-config.json";

import './MapWidget.css';

// These next two lines get around an issue with the latest version of mapbox-gl. Don't ask me how it works.
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

// Access token
mapboxgl.accessToken = mapboxgl_config.access_token;


class MapWidget extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};

        this.partitioner = props['partitioner'];

        this.map = null;

        this.addTornadoes = this.addTornadoes.bind(this);
        this.addEnvironmentData = this.addEnvironmentData.bind(this);
        this.torsToLayers = this.torsToLayers.bind(this);

        this.is_ready = false;
        this.is_loaded = false;

        this.env_layers = [];
        this.popups = [];
    }
    
    async componentDidUpdate(prev_props) {
        if (!this.is_loaded) {
            return;
        }

        if (prev_props['controls_visible'] != this.props['controls_visible']) {
            if (this.props['controls_visible']) {
                this.mapContainer.classList.remove('controls-hidden');
            }
            else {
                this.mapContainer.classList.add('controls-hidden');
            }
            this.map.resize();
        }

        const arrays_equal = (a, b) => {
            return a.length == b.length && [...zip(a, b)].map(([ae, be]) => ae == be).reduce((a, b) => a && b, true);
        }

        if (this.props['env_type'] != prev_props['env_type']) {
            this.addEnvironmentData();
            this.updateEnvironmentTimes();
        }

        if (!arrays_equal(this.props['env_data_times'], prev_props['env_data_times'])) {
            if (prev_props['sel_data_time'] === null || this.props['sel_data_time'] === null) {
                this.addEnvironmentData();
            }
            this.updateEnvironmentTimes();
        } 

        if (this.props['sel_data_time'] !== prev_props['sel_data_time']) {
            this.updateSelectedTime(prev_props['sel_data_time']);
        }

        if (this.props['fit_bounds'] != prev_props['fit_bounds']) {
            this._setViewport(this.props['fit_bounds'], false);
        }

        if ((this.props['interval'] === null && prev_props['interval'] === null 
            || this.props['interval'] !== null && prev_props['interval'] !== null && this.props['interval'].equals(prev_props['interval']))
            && this.props['part_select'] == prev_props['part_select'] && this.props['tor_coll'] === prev_props['tor_coll']) {
            return;
        }

        if (this.props['onloadingstart']) this.props['onloadingstart']('drawing_tors');
        var layers = await this.torsToLayers();
        const tors_visible = Object.entries(layers).filter(([k, lyr]) => k.endsWith('-lines'))
                                   .map(([k, lyr]) => lyr['features'].map(feat => feat['properties']['id'])).flat();

        this.popups.forEach(pu => {
            if (!tors_visible.includes(pu.tor_id)) {
                pu.remove();  // Popups remove themselves from the list when they get closed
            }
        });

        // Set data for each layer on the map (much cheaper than destroying and remaking each layer)
        Object.entries(layers).forEach(([layer_id, layer_json]) => {
            this.map.getSource(layer_id).setData(layer_json);
        });

        if (this.props['onloadingend']) this.props['onloadingend']('drawing_tors');
    }

    componentDidMount() {
        this.map = new mapboxgl.Map({
            container: this.mapContainer,
            style: 'mapbox://styles/tsupinie/cl1yjg05m000614o5afb3ryh0',
    
            maxZoom: 14,
            //antialias: true, // Antialiasing kills performance on Safari
        });

        if (!this.props['controls_visible']) {
            this.mapContainer.classList.add('controls-hidden');
        }

        this.map.on('move', () => {
            const lon = this.map.getCenter().lng;
            const lat = this.map.getCenter().lat;
            const zoom = this.map.getZoom();

            if (this.props['onmove'] !== undefined) {
                this.props['onmove']({'center_lat': lat, 'center_lon': lon, 'zoom': zoom});
            }
        });

        if (this.props['map_position'] === null) {
            this._setViewport(this.props['fit_bounds'], true);
        }
        else {
            const map_pos = this.props['map_position'];
            this.map.setZoom(map_pos['zoom'], {'animate': false});
            this.map.setCenter([map_pos['center_lon'], map_pos['center_lat']], {'animate': false});            
        }
    
        this.map.on('error', event => {});

        this.map.on('load', () => this.is_loaded = true)
        this.map.on('load', this.addTornadoes);
        this.map.on('load', () => {
            this.addEnvironmentData();
            this.updateEnvironmentTimes();
        });
        this.map.on('load', () => this.updateSelectedTime());

        this.map.on('idle', () => {
            this.is_ready = true;
            if (this.props['onmapready'] !== undefined) {
                this.props['onmapready']();
            }
        });

        this.map.on('render', () => {
            if (this.is_ready) {
                if (this.props['onmapnotready'] !== undefined) {
                    this.props['onmapnotready']();
                }
            }
            this.is_ready = false;
        });

        this.props['setscreenshotfunc'](this.screenshot.bind(this), 'map');
    }

    addEnvironmentData() {
        this.env_layers.map(layer => {
            if (this.map.getLayer(layer.id) !== undefined) {
                this.map.removeLayer(layer.id);
            }
        });

        if (this.props['env_type'] !== null && this.props['env_data_times'] !== null && this.props['sel_data_time'] !== null) {
            const layers = this.props['env_handler'].getLayers(this.props['env_type']);
        
            layers.map(layer => {
                layer.setDatetime(this.props['sel_data_time']);
                this.map.addLayer(layer, 'tunnel-street-minor-low');
            });

            this.env_layers = layers;
        }
        else {
            this.env_layers = [];
        }
    }

    updateEnvironmentTimes() {
        if (this.props['env_data_times'] !== null && this.props['sel_data_time'] !== null) {
            this.props['env_handler'].populateLayers(this.env_layers, this.props['env_data_times'], this.props['sel_data_time'],
                                                     this.props['reanalysis_domain'], this.props['reanalysis_src']);
        }
    }

    updateSelectedTime() {
        if (this.props['sel_data_time'] !== null) {
            this.env_layers.forEach(layer => {
                layer.setDatetime(this.props['sel_data_time']);
            });
        }
    }

    _setViewport(bounds, is_initial) {
        this.map.setMinZoom(1);
        this.map.fitBounds(bounds,
            {'animate': !is_initial, 'padding': {'top': 55, 'bottom': 15, 'left': 15, 'right': 15}}
        );
    }

    async addTornadoes() {
        var layers = await this.torsToLayers();
        const track_markers = layers['track-markers'];
        delete layers['track-markers'];

        const color_blend = (color1, color2) => {
            const float_color1 = hex2rgba(color1);
            const float_color2 = hex2rgba(color2);

            const alpha1 = float_color1[3];
            const alpha2 = float_color2[3];
            const alpha_blend = alpha1 + alpha2 * (1 - alpha1);
            let blend = [...zip(float_color1, float_color2)].slice(0, -1).map(([c1, c2]) => (c1 * alpha1 + c2 * alpha2 * (1 - alpha1)) / alpha_blend);
            blend.push(alpha_blend);

            return rgba2hex(blend);
        }

        const add_layer = ([layer_id, layer_json]) => {
            // Add layer to the map
            const split_idx = layer_id.lastIndexOf('-')
            const pk = layer_id.substring(0, split_idx);
            const layer_type = layer_id.substring(split_idx + 1);

            const color = this.partitioner.colors[this.partitioner.part_keys.indexOf(pk)];

            var type, layout, paint;

            if (layer_type == 'points') {
                type = 'circle';
                layout = {};
                paint = {
                    'circle-color': color,
                    'circle-radius': 4,
                };
            }
            else if (layer_type == 'polys') {
                const poly_opacity = 0.5;
                const poly_opacity_hex = Math.round(poly_opacity * 255).toString(16).padStart(2, '0');
                const poly_color = color_blend(color + poly_opacity_hex, '#818888ff').slice(0, -2);

                type = 'fill'
                layout = {};
                paint = {
                    'fill-color': poly_color,
                    'fill-opacity': 1.0,
                }
            }
            else if (layer_id.includes('track-markers')) {
                type = 'circle';
                layout = {};
                paint = {
                    'circle-color': '#ffffff',
                    'circle-radius': 2,
                    'circle-opacity': 0.6,
                };
            }
            else {
                type = 'line';
                layout = {'line-cap': 'round'};
                paint = {
                    'line-color': color,
                    'line-width': 6,
                };
            }

            this.map.addLayer({
                id: layer_id,
                source: {
                    type: 'geojson',
                    data: layer_json,
                    tolerance: 0.01,
                },
                type: type,
                layout: layout,
                paint: paint
            }, 'tunnel-street-minor-case');

            // Set click handler for each tornado (creates a short table)
            this.map.on('click', layer_id, async event => {
                const target_feature = event.features[0];
                if (!target_feature['properties']['popup']) {
                    return;
                }

                var tor_coords = target_feature.geometry.coordinates.slice();
                if (target_feature.geometry.type == 'Point') {
                    var coordinates = tor_coords;
                }
                else if (target_feature.geometry.type == 'LineString') {
                    let path_length = [...pairwise(tor_coords)].map(([pt_a, pt_b]) => Math.hypot(pt_a[0] - pt_b[0], pt_a[1] - pt_b[1]));
                    let path_length_accum = [0];
                    path_length.forEach(seg => {
                        path_length_accum.push(path_length_accum[path_length_accum.length - 1] + seg);
                    });

                    const target_path_length = path_length_accum[path_length_accum.length - 1] / 2;
                    const target_idx = path_length_accum.indexOf([...pairwise(path_length_accum)]
                                                                 .filter(([a, b]) => a <= target_path_length && b >= target_path_length)[0][0]);

                    const pt1 = tor_coords[target_idx];
                    const pt2 = tor_coords[target_idx + 1];
                    let alpha;
                    if (path_length_accum[target_idx] == path_length_accum[target_idx + 1]) {
                        alpha = 0.5;
                    }
                    else {
                        alpha = (target_path_length - path_length_accum[target_idx]) 
                            / (path_length_accum[target_idx + 1] - path_length_accum[target_idx]);
                    }

                    var coordinates = [(pt1[0] * (1 - alpha) + pt2[0] * alpha),
                                       (pt1[1] * (1 - alpha) + pt2[1] * alpha)];
                }
                else {
                    var coordinates = tor_coords[0].reduce((a, b) => {
                        return [parseFloat(a[0]) + parseFloat(b[0]), parseFloat(a[1]) + parseFloat(b[1])];
                    }, [0, 0]).map(c => c / tor_coords[0].length);
                }

                const tor_props = await this.props['tor_coll'].getGeoJSONById(target_feature['properties']['id']);

                // For GeometryCollections, the centerline should always be the first geometry
                const tor_geometry = tor_props['geometry']['type'] == 'GeometryCollection' ? tor_props['geometry']['geometries'][0] : tor_props['geometry'];

                const width_units = this.props['units'] == 'metric' ? 'm' : 'yds';
                const length_units = this.props['units'] == 'metric' ? 'km' : 'mi';

                const table = {
                    'datetime-start': {'header': 'Start Time', 'is_prop': true, 'units': ''},
                    0: {'header': 'Start Location', 'is_prop': false, 'units': ''},
                    1: {'header': 'End Location', 'is_prop': false, 'units': ''},
                    'f-scale': {'header': 'Rating', 'is_prop': true, 'units': ''},
                    'path-length': {'header': 'Path Length', 'is_prop': true, 'units': length_units},
                    'path-width': {'header': 'Path Width', 'is_prop': true, 'units': width_units},
                    'fatalities': {'header': 'Fatalities', 'is_prop': true, 'units': ''},
                    'injuries': {'header': 'Injuries', 'is_prop': true, 'units': ''},
                    'source': {'header': 'Source', 'is_prop': true, 'units': ''},
                };

                var description = "<table>"
                for (let key_ in table) {
                    let info = table[key_];
                    let header = info['header'];

                    const key = key_ == 1 ? tor_geometry['coordinates'].length - 1 : key_;

                    if (key == 1 && tor_geometry['type'] == 'Point') {
                        continue;
                    }

                    let prop_loc = info['is_prop'] ? tor_props['properties'] : tor_geometry['coordinates'];
                    if (!info['is_prop'] && tor_geometry['type'] == 'Polygon') {
                        prop_loc = prop_loc[0];
                    }
                    let prop;
                    if (key == 0 && tor_geometry['type'] == 'Point') {
                        prop = prop_loc;
                        info['header'] = 'Location';
                    }
                    else {
                        prop = prop_loc[key];
                    }

                    if (prop === undefined) {
                        continue;
                    }

                    if (key == 'datetime-start') {
                        const dt = DateTime.fromFormat(prop, "yyyy-MM-dd'T'hh:mm:ss'Z'", {'zone': 'utc'});

                        const click_intv_start = dt.minus(this.props['cday_offset']).startOf('day').plus(this.props['cday_offset']);
                        const click_intv_end = dt.minus(this.props['cday_offset']).endOf('day').plus(this.props['cday_offset'])
                                                 .plus({'millisecond' : 1});
                        const click_intv = Interval.fromDateTimes(click_intv_start, click_intv_end);

                        const date = dt.toLocaleString(DateTime.DATE_MED)
                        const time = dt.toFormat('HHmm') + ' UTC ';
                        const date_link = '<a class="popup-link" data-clickintv="' + click_intv.toISO() + '" href="#">' 
                                            + date + '</a>';

                        if (!timeIsKnown(tor_props['properties'])) {
                            prop = date_link;
                            header = 'Date';
                        }
                        else {
                            prop = time + date_link;
                        }
                    }

                    if (key == 'f-scale') {
                        let scale;
                        if (tor_props['properties']['scale'] === undefined) {
                            scale = 'F'
                            let dt = DateTime.fromISO(tor_props['properties']['datetime-start']);
                            if (dt > DateTime.utc(2007, 2, 1, 0)) {
                                scale = 'EF';
                            }
                        }
                        else {
                            scale = tor_props['properties']['scale'];
                        }
                        
                        if (Array.isArray(prop)) {
                            prop = Math.max(...prop);
                        }
                        prop = scale + (prop == -9 ? 'U' : prop);
                    }

                    if (key == 'path-length') {
                        if (info['units'] == 'km') {
                            prop = prop * 1.609; // Convert miles to kilometers
                        }
                        prop = Math.round(prop * 10) / 10;
                    }

                    if (key == 'path-width') {
                        if (info['units'] == 'm') {
                            prop = prop * 0.9144; // Convert yards to meters
                        }
                        prop = Math.round(prop);
                    }

                    if (key_ == 0 || key_ == 1) {
                        let [lon, lat] = prop.map(c => Math.round(c * 100) / 100);
                        lon = lon < 0 ? -lon + '\u00b0 W' : lon + '\u00b0 E';
                        lat = lat < 0 ? -lat + '\u00b0 S' : lat + '\u00b0 N';

                        prop = lat + ', ' + lon;
                    }

                    description += "<tr><th>" + header + ":</th>" + 
                                    "<td>" + prop + (info['units'] == '' ? '' : ' ' + info['units']) + "</tr>";
                }
                description += "</table>";
                
                // Ensure that if the map is zoomed out such that multiple
                // copies of the feature are visible, the popup appears
                // over the copy being pointed to.
                while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
                }
                
                const popup = new mapboxgl.Popup({'maxWidth': '400px'})
                                          .setLngLat(coordinates)
                                          .setHTML(description)
                                          .addTo(this.map);

                popup.tor_id = target_feature['properties']['id'];

                popup.on('close', () => { this.popups = this.popups.filter(pu => pu != popup); });
                this.popups.push(popup);

                [...document.getElementsByClassName('popup-link')].forEach(link_elem => {
                    link_elem.addEventListener('click', ev => {
                        ev.preventDefault();
                        if (this.props['onintervalchange'] !== undefined) {
                            const intv = Interval.fromISO(link_elem.dataset.clickintv, {'zone': 'utc'});
                            this.props['onintervalchange'](new SelectionInterval(intv), true);
                        }
                    });
                });
            });

            if (!layer_id.includes('polys')) {
                // Set mouse behavior for this layer
                this.map.on('mouseenter', layer_id, event => {
                    this.map.getCanvas().style.cursor = 'pointer';
                });

                this.map.on('mouseleave', layer_id, event => {
                    this.map.getCanvas().style.cursor = '';
                });
            }
        }

        let poly_layers = Object.fromEntries(Object.entries(layers).filter(([layer_id, layer_json]) => layer_id.includes('polys')));
        layers = Object.fromEntries(Object.entries(layers).filter(([layer_id, layer_json]) => !layer_id.includes('polys')));

        Object.entries(poly_layers).forEach(add_layer);
        Object.entries(layers).forEach(add_layer);
    }

    async torsToLayers() {
        var layers = {};

        const GEOM_TYPES = {'point': 'points', 'linestring': 'lines', 'polygon': 'polys'}

        this.partitioner.part_keys.forEach(pk => {
            layers[pk + '-lines'] = {'type': 'FeatureCollection', 'features': []};
            layers[pk + '-points'] = {'type': 'FeatureCollection', 'features': []};
            layers[pk + '-polys'] = {'type': 'FeatureCollection', 'features': []};
            layers[pk + '-track-markers'] = {'type': 'FeatureCollection', 'features': []}
        });

        if (this.props['tor_coll'] === null || this.props['interval'] === null) {
            return layers;
        }

        var tors = await this.props['tor_coll'].getGeoJSON(this.props['interval'], this.partitioner);
        var partition_keys = Object.keys(tors);

        partition_keys.forEach((pk, i) => {
            let feat_coll = tors[pk];
            feat_coll['features'].forEach(feat => {
                feat['properties']['popup'] = true;

                let layer_id = pk + '-' + GEOM_TYPES[feat['geometry']['type'].toLowerCase()];
                if (feat['geometry']['type'] == 'Polygon') {
                    feat['properties']['popup'] = false;
                    if ( feat['geometry']['coordinates'].length > 1) {
                        feat['geometry']['coordinates'] = [feat['geometry']['coordinates']];
                    }
                }

                layers[layer_id]['features'].push(feat);

                if (feat['geometry']['type'] != 'Polygon') {
                    let pt_feat = {'properties': {}};

                    Object.assign(pt_feat['properties'], feat['properties']);
                    pt_feat['properties']['popup'] = false;
    
                    if (feat['geometry']['type'] != 'Point') {
                        pt_feat['geometry'] = {'type': 'Point', 'coordinates': feat['geometry']['coordinates'][0]}
                    }
                    else {
                        pt_feat['geometry'] = {'type': 'Point', 'coordinates': feat['geometry']['coordinates']}
                    }
    
                    layers[pk + '-track-markers']['features'].push(pt_feat);
                }
            });
        });

        return layers;
    }

    screenshot() {
        const takeScreenshot = map => {
            return new Promise((resolve, reject) => {
                map.once('render', () => {
                    let canvas = document.createElement('canvas');
                    const map_canvas = map.getCanvas();

                    canvas.width = map_canvas.width;
                    canvas.height = map_canvas.height;

                    let ctx = canvas.getContext('2d');
                    ctx.drawImage(map_canvas, 0, 0);
                    resolve(canvas);
                });

                // Trigger a render
                map.setBearing(map.getBearing());
            });
        }

        return takeScreenshot(this.map);
    }

    render() {
        return (
            <div ref={el => this.mapContainer = el} className="mapContainer" />
        )
    }
}

export default MapWidget;
